import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { REP_ROLES } from "../../utils/constants";
import Dropdown from "../inputs/Dropdown";
import Input from "../inputs/Input";
import Modal from "../shared/Modal";

// Validation schema for adding new rep
const addRepSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  repRole: z.string().min(1, "Rep role is required"),
});

const AddRepModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  canAddRep,
  requirements,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(addRepSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      repRole: "",
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Rep"
      size="md"
      onConfirm={handleSubmit(handleFormSubmit)}
      onCancel={handleClose}
      confirmText={loading ? "Adding..." : "Add Rep"}
      cancelText="Cancel"
      loading={loading}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              label="Full Name"
              field={field}
              name="name"
              type="text"
              placeholder="Enter full name"
              required
              error={errors.name}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              label="Email Address"
              field={field}
              name="email"
              type="email"
              placeholder="Enter email address"
              required
              error={errors.email}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Input
              label="Password"
              field={field}
              name="password"
              type="password"
              placeholder="Enter password"
              required
              isPassword
              error={errors.password}
            />
          )}
        />

        <Controller
          name="repRole"
          control={control}
          render={({ field }) => (
            <Dropdown
              headLabel="Rep Role"
              label="Select rep role"
              options={REP_ROLES.filter((role) =>
                canAddRep ? canAddRep(role.value) : true
              )}
              value={field.value}
              onSelect={field.onChange}
              required
              error={errors.repRole}
            />
          )}
        />

        {requirements && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-blue-900 mb-2">
              Rep Requirements for {requirements.description}
            </h4>
            <div className="text-sm text-blue-800">
              <p>Community Members: {requirements.communityReps} required</p>
              <p>FEMA Members: {requirements.clientReps} required</p>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> A new user account will be created with the
            provided credentials. The rep will be automatically added to this
            panel.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default AddRepModal;
